import { useEffect, useState } from "react";
import { Save, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import SPShell from "@/components/SPShell";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";

const PROVIDER_LABELS = {
  INSURANCE_BROKER: "Insurance broker",
  LAWYER: "Lawyer",
  BUILDING_SURVEYOR: "Building surveyor",
  STRATA_PLUMBER: "Strata plumber",
  STRATA_ELECTRICIAN: "Strata electrician",
  FACILITIES_MGR: "Facilities manager",
  OTHER: "Other",
};

export default function SPProfile() {
  const [sp, setSp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    business_name: "",
    contact_email: "",
    contact_phone: "",
    service_areas_csv: "",
    public_profile: "",
  });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/sp/me");
        if (!mounted) return;
        setSp(data);
        setForm({
          business_name: data.business_name || "",
          contact_email: data.contact_email || "",
          contact_phone: data.contact_phone || "",
          service_areas_csv: (data.service_areas || []).join(", "),
          public_profile: data.public_profile || "",
        });
      } catch (e) {
        if (mounted) setError(apiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        business_name: form.business_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
        service_areas: form.service_areas_csv
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
        public_profile: form.public_profile.trim(),
      };
      const { data } = await api.patch("/sp/me", body);
      setSp(data);
      toast.success("Profile saved");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SPShell>
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-8" data-testid="sp-profile-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--navy)" }} data-testid="sp-profile-heading">
            Business profile
          </h1>
          <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
            Owners and chairs see this when your name appears alongside a quote response.
          </p>
        </div>

        {error && (
          <div
            className="rounded-lg p-4 text-sm mb-6"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="sp-profile-error"
          >
            {error}
          </div>
        )}

        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : sp ? (
          <>
            {/* Locked admin-managed facts */}
            <div
              className="rounded-xl border p-5 mb-5"
              style={{ borderColor: "var(--border)", background: "#f8fafc" }}
              data-testid="sp-profile-locked"
            >
              <div className="flex items-center gap-2 mb-3">
                <Lock size={14} style={{ color: "var(--slate)" }} />
                <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--slate)" }}>
                  Managed by Opstrait admin
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                    Provider type
                  </div>
                  <div className="font-medium" style={{ color: "var(--navy)" }} data-testid="sp-profile-provider-type">
                    {PROVIDER_LABELS[sp.provider_type] || sp.provider_type}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                    ABN
                  </div>
                  <div className="font-medium" style={{ color: "var(--navy)" }} data-testid="sp-profile-abn">
                    {sp.abn || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                    Insurer partner
                  </div>
                  <div className="font-medium" style={{ color: "var(--navy)" }} data-testid="sp-profile-insurer-partner">
                    {sp.insurer_partner ? "Yes" : "No"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--slate)" }}>
                    Verified
                  </div>
                  <div
                    className="font-medium inline-flex items-center gap-1"
                    style={{ color: sp.verified ? "#166534" : "var(--slate)" }}
                    data-testid="sp-profile-verified"
                  >
                    {sp.verified && <CheckCircle2 size={13} />}
                    {sp.verified ? "Verified" : "Pending verification"}
                  </div>
                </div>
              </div>
              <p className="text-[11px] mt-3" style={{ color: "var(--slate)" }}>
                Need to change any of these? Email{" "}
                <a href="mailto:admin@opstrai.com" className="underline">admin@opstrai.com</a>.
              </p>
            </div>

            {/* Editable fields */}
            <form
              onSubmit={onSubmit}
              className="rounded-xl bg-white border p-5 space-y-4"
              style={{ borderColor: "var(--border)" }}
              data-testid="sp-profile-form"
            >
              <div>
                <Label htmlFor="business_name" className="text-xs font-semibold mb-1.5 block">
                  Business name
                </Label>
                <Input
                  id="business_name"
                  value={form.business_name}
                  onChange={onChange("business_name")}
                  data-testid="sp-profile-business-name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contact_email" className="text-xs font-semibold mb-1.5 block">
                    Contact email
                  </Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={form.contact_email}
                    onChange={onChange("contact_email")}
                    data-testid="sp-profile-contact-email"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone" className="text-xs font-semibold mb-1.5 block">
                    Contact phone
                  </Label>
                  <Input
                    id="contact_phone"
                    type="tel"
                    value={form.contact_phone}
                    onChange={onChange("contact_phone")}
                    data-testid="sp-profile-contact-phone"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="service_areas" className="text-xs font-semibold mb-1.5 block">
                  Service areas <span className="font-normal text-slate-500">(comma-separated, e.g. &ldquo;NSW, Sydney metro&rdquo;)</span>
                </Label>
                <Input
                  id="service_areas"
                  value={form.service_areas_csv}
                  onChange={onChange("service_areas_csv")}
                  data-testid="sp-profile-service-areas"
                />
              </div>
              <div>
                <Label htmlFor="public_profile" className="text-xs font-semibold mb-1.5 block">
                  Public profile <span className="font-normal text-slate-500">(shown to chairs when reviewing responses)</span>
                </Label>
                <Textarea
                  id="public_profile"
                  value={form.public_profile}
                  onChange={onChange("public_profile")}
                  rows={5}
                  placeholder="Short bio: years in strata insurance, specialities, AFSL number, response time SLAs…"
                  data-testid="sp-profile-public-bio"
                />
              </div>
              <Button
                type="submit"
                disabled={saving}
                style={{ background: "var(--blue)", color: "white" }}
                data-testid="sp-profile-save"
              >
                <Save size={14} className="mr-1.5" />
                {saving ? "Saving…" : "Save profile"}
              </Button>
            </form>
          </>
        ) : null}
      </div>
    </SPShell>
  );
}
